<template>
  <v-dialog :value="true" max-width="500" persistent>
    <v-card>
      <v-card-title>Reporte mensual — {{ contractorCompany.contractor.name }}</v-card-title>
      <v-card-text>
        <div class="caption grey--text text--darken-1 mb-2">
          Las etiquetas tildadas salen en las Observaciones de la planilla, con su vencimiento.
        </div>
        <v-progress-linear v-if="loading" indeterminate class="mb-2" />
        <div v-if="!loading && !tags.length" class="caption grey--text text--darken-1 mb-2">
          Esta contratista no tiene obligaciones etiquetadas.
        </div>
        <v-checkbox
          v-for="tag in tags"
          :key="tag._id"
          v-model="selected"
          :value="tag._id"
          :label="tag.reportLabel || tag.name"
          :disabled="saving"
          dense
          hide-details
        />
        <v-textarea
          v-model="comment"
          class="mt-4"
          label="Observaciones del reporte de fin de mes"
          :hint="notesOnly ? 'Salen en la planilla.' : 'Salen en la planilla, debajo de las obligaciones vencidas.'"
          persistent-hint
          rows="1"
          auto-grow
          dense
          :disabled="saving"
        />
        <v-checkbox
          v-model="notesOnly"
          class="mt-2"
          label="Solo incluir las observaciones detalladas arriba para el reporte de fin de mes"
          :disabled="saving"
          dense
          hide-details
        />
        <v-select
          v-model="previousStatus"
          :items="previousStatusOptions"
          class="mt-4"
          label="Status anterior"
          hint="Se usa mientras no haya un envío real del mes anterior; después manda lo que se envió."
          persistent-hint
          dense
          :disabled="saving"
        />
        <v-alert v-if="errorMessage" type="error" dense text>{{ errorMessage }}</v-alert>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn :disabled="saving" @click="$emit('close')">{{ resx.labels.cancel }}</v-btn>
        <v-btn class="primary" :loading="saving" :disabled="loading" @click="save">{{ resx.labels.ok }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
"use strict";

import tagsService from "@/services/TagsService";
import contractorCompanyService from "@/services/ContractorCompanyService";

const idOf = value => (value && value._id) || value;

export default {
  name: "report-settings-dialog",
  props: {
    contractorCompany: { type: Object, required: true }
  },
  data() {
    return {
      resx: this.$store.state.locale.resx,
      tags: [],
      selected: [],
      comment: "",
      notesOnly: false,
      previousStatus: "",
      previousStatusOptions: [
        { text: "Automático", value: "" },
        { text: "Apto", value: "suitable" },
        { text: "No Apto", value: "notSuitable" }
      ],
      loading: false,
      saving: false,
      errorMessage: null
    };
  },
  async mounted() {
    this.loading = true;
    try {
      // El listado no trae las obligaciones completas: se pide la ficha.
      const [tags, detail] = await Promise.all([
        tagsService.list(),
        contractorCompanyService.getById(this.contractorCompany._id)
      ]);
      const used = new Set();
      (detail.obligations || []).forEach(o => (o.tags || []).forEach(t => used.add(idOf(t))));
      this.selected = (detail.reportTags || []).map(idOf);
      this.selected.forEach(id => used.add(id));
      this.tags = tags.filter(t => used.has(t._id));
      this.comment = detail.reportNotes || "";
      this.notesOnly = !!detail.reportNotesOnly;
      this.previousStatus = detail.reportPreviousStatus || "";
    } catch (error) {
      this.errorMessage = this.messageOf(error, "No se pudo cargar el reporte mensual");
    } finally {
      this.loading = false;
    }
  },
  methods: {
    messageOf(error, fallback) {
      return (error.response && error.response.data && error.response.data.message) || fallback;
    },
    async save() {
      if (this.saving) return;
      this.errorMessage = null;
      this.saving = true;
      try {
        // Una etiqueta borrada que siga guardada no se muestra ni se conserva.
        const live = this.selected.filter(id => this.tags.some(t => t._id === id));
        await contractorCompanyService.saveReportSettings(this.contractorCompany._id, {
          tags: live,
          comment: this.comment,
          notesOnly: this.notesOnly,
          previousStatus: this.previousStatus
        });
        this.$emit("close");
      } catch (error) {
        this.errorMessage = this.messageOf(error, "No se pudo guardar el reporte mensual");
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>
